import React, { useState, lazy, Suspense } from 'react';

// Hooks
import useCustomerLogin from '@src/hooks/useCustomerLogin';
import useCustomerAccessToken from '@src/hooks/useCustomerAccessToken';

// Comps
const AccountForgotPassword = lazy(
    () => import('@project-stories/ecommerce/Account/AccountForgotPassword/AccountForgotPassword')
);
import {TextField} from '@arctheme-components/elements/TextField/TextField';
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@arctheme-components/elements/Paragraph/Paragraph';
import ButtonSpinner from '@src/stories/elements/ButtonSpinner/ButtonSpinner';

const AccountLoginForm = ({ section }: { section: any }) => {
    const {
        email_label,
        email_placeholder,
        password_label,
        password_placeholder,
        button_label_login,
        button_style_login,
        button_label_forgot_password
    } = section;

    // States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    // Hooks
    const { loginCustomer, loading: loginLoading, error, success } = useCustomerLogin();
    const {
        getAccessToken,
        loading: accessLoading,
        errorMessage: accessError,
        successMessage: accessSuccess
    } = useCustomerAccessToken();

    const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Check if the pair of email and password exists in the system
        const data: any = await getAccessToken(email, password);

        // If exists, then sign in
        if (data.success) {
            await loginCustomer(email, password);
        }
    };

    const toggleForgotPassword = (event: React.ClickEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setShowForgotPassword(!showForgotPassword);
    };

    const showSignUpCta = true;
    const showSuccess = false;

    return (
        <>
            {!showForgotPassword && (
                <form
                    data-shopify-captcha='true'
                    id='loginForm'
                    onSubmit={handleSubmit}
                    className='account-page__form'
                >
                    <div className='flex flex-col gap-y-4'>
                        <div className={`w-full grid grid-cols-2`}>
                            <h4 className={`w-full text-left`}>LOG IN</h4>
                            <p className={`b2 w-full text-right text-[#C0C0C0]`}>*Required field</p>
                        </div>

                        {(error || accessError) && (
                            <Paragraph
                                text={error || accessError}
                                type='errors'
                            />
                        )}

                        <TextField
                            className={`h4 w-full`}
                            inputType='email'
                            id='emailLogin'
                            name='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required={true}
                            label={email_label}
                            placeholder={`${email_placeholder}*`}
                        />

                        <TextField
                            className={`h4 w-full`}
                            inputType='password'
                            id='passwordLogin'
                            name='password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required={true}
                            label={password_label}
                            placeholder={`${password_placeholder}*`}
                        />


                        {showSuccess && (success || accessSuccess) && (
                            <Paragraph
                                text={success || accessSuccess}
                                type='success text-center'
                            />
                        )}

                        <Link
                            link='#'
                            onClick={toggleForgotPassword}
                            classes='w-full b4 text-left link--underlined text-[#696969]'
                            ariaLabel='Go to reset password page'
                        >
                            {button_label_forgot_password}
                        </Link>

                        <ButtonSpinner
                            isLoading={loginLoading || accessLoading}
                            classes={`button button-${button_style_login} w-full h-[calc(3rem-2px)] text-base`}
                            ariaLabel='Submit login form'
                            loadingText={'Logging in...'}
                        >
                            {button_label_login}
                        </ButtonSpinner>

                        {showSignUpCta && (
                            <div className='text-center font-light w-full lg:w-[360px] py-[16px]'>
                                <div className='account-inquiry flex flex-col my-2 text-left space-y-[20px]'>
                                    <h4>CREATE AN ACCOUNT</h4>
                                    <span className={`b4`}>
                                    Create an account for an easy checkout, save addresses and wishlists and view your order history. You can also sign up to the Lee Mathews mailing list to receive updates on new arrivals and access to secret sales.

                                  </span>
                                    <a className={`button button-primary w-full`} href='/account/register'>
                                        Register
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Social login app snippet */}
                        <div className='one-click-social-login'></div>
                    </div>
                </form>
            )}

            {showForgotPassword && (
                <Suspense>
                    <AccountForgotPassword
                        onCancel={toggleForgotPassword}
                        section={section}
                    />
                </Suspense>
            )}
        </>
    );
};

export default AccountLoginForm;
